import type { FormControl } from '@angular/forms';
import type {
  ContestEntriesCollection,
  ContestsCollection,
  ManageViewContestHandoversCollection,
  ManageViewContestsCollection,
  ManageViewHandoversCollection,
  ManageViewOngoingBidsCollection,
  ManageViewOngoingProjectsCollection,
  ManageViewOpenBidsCollection,
  ManageViewOpenProjectsCollection,
  ManageViewPastBidsCollection,
  ManageViewPastProjectsCollection,
  MilestonesCollection,
  ProjectsCollection,
  ProjectViewProjectsCollection,
  UsersCollection,
} from '@freelancer/datastore/collections';

export enum ManageTableType {
  CONTEST = 'contest',
  PROJECT = 'project',
  HANDOVER = 'handover',
  QUOTES = 'quotes',
  SERVICE_OFFERINGS = 'service_offerings',
}

export enum ManageTableView {
  OPEN = 'open',
  ONGOING = 'ongoing',
  PAST = 'past',
}

export enum ManageViewType {
  EMPLOYER = 'employer',
  FREELANCER = 'freelancer',
  COLLABORATOR = 'collaborator',
  /** includes both employer and freelancer tables, with no contests */
  INSOURCE = 'insource',
}

export type ManageCollectionType =
  | ContestEntriesCollection
  | ContestsCollection
  | ManageViewContestHandoversCollection
  | ManageViewContestsCollection
  | ManageViewHandoversCollection
  | ManageViewOpenBidsCollection
  | ManageViewOngoingBidsCollection
  | ManageViewPastBidsCollection
  | ManageViewOpenProjectsCollection
  | ManageViewOngoingProjectsCollection
  | ManageViewPastProjectsCollection
  | MilestonesCollection
  | ProjectsCollection
  | ProjectViewProjectsCollection
  | UsersCollection;

export enum ManageProjectFilter {
  ALL = 'All',
  RECRUITER = 'Recruiter',
  TOKEN = 'Token', // used for insource tables, not visible to the user
  EVERYTHING = 'Everything', // used for deloitte tables, fetches both internal and external projects
}

export const manageProjectFilterOptions: readonly ManageProjectFilter[] = [
  ManageProjectFilter.ALL,
  ManageProjectFilter.RECRUITER,
];

export enum ManageQuotesFilter {
  ALL = 'All',
  ACCEPTED = 'Accepted',
  REJECTED = 'Rejected',
  PENDING = 'Pending',
  EXPIRED = 'Expired',
  TEMPLATE = 'Template',
}

export const manageQuotesFilterOptions: readonly ManageQuotesFilter[] = [
  ManageQuotesFilter.ALL,
  ManageQuotesFilter.ACCEPTED,
  ManageQuotesFilter.REJECTED,
  ManageQuotesFilter.PENDING,
  ManageQuotesFilter.EXPIRED,
  ManageQuotesFilter.TEMPLATE,
];

export enum ManageServiceOfferingsFilter {
  ALL = 'All',
  DRAFT = 'Draft',
  ACTIVE = 'Active',
  HIDDEN = 'Hidden',
}

export const manageServiceOfferingsFilterOptions: readonly ManageServiceOfferingsFilter[] =
  [
    ManageServiceOfferingsFilter.ALL,
    ManageServiceOfferingsFilter.DRAFT,
    ManageServiceOfferingsFilter.ACTIVE,
  ];

export const itemsPerPageOptions: readonly string[] = ['10', '20', '50', '100'];

export interface ManageProjectSearchFilter {
  readonly query: string;
  readonly filter?: ManageProjectFilter;
}

export interface ManageQuotesSearchFilter {
  readonly query: string;
  readonly quotesFilter?: ManageQuotesFilter;
}

export interface ManageServiceOfferingsSearchFilter {
  readonly query: string;
  readonly serviceOfferingsFilter?: ManageServiceOfferingsFilter;
}

export const defaultProjectSearchFilter = {
  query: '',
  filter: ManageProjectFilter.ALL,
};

export const defaultQuotesSearchFilter = {
  query: '',
  quotesFilter: ManageQuotesFilter.ALL,
};

export const defaultServiceOfferingsSearchFilter = {
  query: '',
  serviceOfferingsFilter: ManageServiceOfferingsFilter.ALL,
};

export const INIT_ITEMS_PER_PAGE = 10;
export const PAGES_TO_REQUEST = 10;
export const PAGES_TO_LOOK_AHEAD = 3;
export const MANAGE_TABLE_ANCHOR = 'manageTop';
export const SEARCH_DEBOUNCE_TIME = 700;

export interface ManageSearchFormGroupType {
  query: FormControl<string>;
  filter: FormControl<ManageProjectFilter>;
  quotesFilter: FormControl<ManageQuotesFilter>;
  serviceOfferingsFilter: FormControl<ManageServiceOfferingsFilter>;
  show: FormControl<string>;
}

export type ManageSearchInsourceFormGroupType = Omit<
  ManageSearchFormGroupType,
  'quotesFilter' | 'serviceOfferingsFilter'
>;
